import type { Contents } from '@common/api/graphql/getContentsGql';
import { ERROR_MESSAGE_FOR_PAGE_VALIDATION } from '@common/middleware/errorMessages';
import {
  addNewGiftConfig,
  deleteGiftConfig,
  initilizeSelectedGiftConfigIndex,
  selectNewGiftConfig,
  setSelectedGiftConfig,
  validateProjectDraftFailure
} from '@console/actions/actions';
import type {
  AppThunkAction,
  AppThunkDispatch
} from '@console/actions/thunkType';
import { getGifteeBoxTemplatesThunk } from '@console/actions/thunks/getGifteeBoxTemplates';
import { getSelectedProjectDraftContentsThunk } from '@console/actions/thunks/getSelectedProjectDraftContents';
import { validateGiftConfigsThunk } from '@console/actions/thunks/validateProjectDraft/validateGiftConfigs';
import { ConfirmationChildSteps, ParentSteps } from '@console/common/constants';
import { GIFTEE_BOX_TEMPLATE_BRAND_NAME } from '@console/common/constants';
import { getActiveStep } from '@console/common/getActiveStep';
import {
  isChoosableGiftConfig,
  isGifteeBoxGiftConfig
} from '@console/common/projectDraft/giftConfig';
import GiftConfigs from '@console/components/projectDraft/GiftConfigs';
import type { AppState } from '@console/reducers';
import type {
  GiftCardGiftConfig,
  GiftConfig,
  GifteeBoxGiftConfig
} from '@console/reducers/projectJson/giftConfigs';
import {
  getGiftCardGiftConfigPrice,
  getGifteeBoxGiftConfigPrice,
  getSelectedContents,
  isApiSolutionSystemSelected,
  isBulkSolutionSystemSelected,
  isGcpSolutionSystemSelected
} from '@console/selectors/getSelectedProjectDraftForDisplay';
import coralAsterisk from 'images/coralAsterisk.svg';
import logo from 'images/logo.png';
import { connect } from 'react-redux';
import type { Location, NavigateFunction } from 'react-router';
import { goNextStep } from './StepHandlerContainer';

export type GiftConfigForDisplay = GiftConfig & {
  firstBrandName: string;
  firstContentName: string;
  thumbnailImageUrl: string;
  price: number;
};

type GiftConfigsForDisplay = GiftConfigForDisplay[];

const getFirstBrandName = (
  contents: Contents,
  giftConfig: GiftConfig
): string => {
  if (isChoosableGiftConfig(giftConfig)) {
    return '';
  }
  return contents[0] ? contents[0].brand.name : '';
};

const getFirstContentName = (
  contents: Contents,
  giftConfig: GiftConfig
): string => {
  if (isChoosableGiftConfig(giftConfig)) {
    return '選べるギフト';
  }
  return contents[0] ? contents[0].internalName : '';
};

const getThumbnailImageUrl = (
  contents: Contents,
  giftConfig: GiftConfig
): string => {
  if (isChoosableGiftConfig(giftConfig)) {
    return coralAsterisk;
  }
  return contents[0] ? contents[0].imageUrl : logo;
};

const getGiftConfigsForDisplay = (
  giftConfigs: GiftConfig[],
  contents: Contents
): GiftConfigsForDisplay =>
  giftConfigs.map(giftConfig => {
    if (isGifteeBoxGiftConfig(giftConfig)) {
      return getGifteeBoxGiftConfigForDisplay(giftConfig);
    } else {
      return getGiftCardGiftConfigForDisplay(giftConfig, contents);
    }
  });

const getGifteeBoxGiftConfigForDisplay = (
  gifteeBoxGiftConfig: GifteeBoxGiftConfig
): GiftConfigForDisplay => ({
  ...gifteeBoxGiftConfig,
  firstBrandName: GIFTEE_BOX_TEMPLATE_BRAND_NAME,
  firstContentName: gifteeBoxGiftConfig.selectedGifteeBoxTemplate?.name || '',
  thumbnailImageUrl:
    gifteeBoxGiftConfig.selectedGifteeBoxTemplate?.imageUrl || '',
  price: getGifteeBoxGiftConfigPrice(gifteeBoxGiftConfig.gifteeBoxInitialPoint)
});

const getGiftCardGiftConfigForDisplay = (
  giftCardGiftConfig: GiftCardGiftConfig,
  contents: Contents
): GiftConfigForDisplay => {
  const selectedContentsForDisplay = getSelectedContents(
    contents,
    giftCardGiftConfig.selectedContents
  );

  return {
    ...giftCardGiftConfig,
    firstBrandName: getFirstBrandName(
      selectedContentsForDisplay,
      giftCardGiftConfig
    ),
    firstContentName: getFirstContentName(
      selectedContentsForDisplay,
      giftCardGiftConfig
    ),
    thumbnailImageUrl: getThumbnailImageUrl(
      selectedContentsForDisplay,
      giftCardGiftConfig
    ),
    price: getGiftCardGiftConfigPrice(selectedContentsForDisplay)
  };
};

const onMount = (): AppThunkAction<void> => dispatch => {
  dispatch(getSelectedProjectDraftContentsThunk()).catch(() => {});
  dispatch(getGifteeBoxTemplatesThunk()).catch(() => {});
};

const createGiftConfig =
  (navigate: NavigateFunction): AppThunkAction<void> =>
  async dispatch => {
    await dispatch(addNewGiftConfig());
    await dispatch(selectNewGiftConfig());
    goGiftConfig(navigate);
  };

const selectGiftConfig =
  (
    navigate: NavigateFunction,
    location: Location,
    index: number
  ): AppThunkAction<void> =>
  dispatch => {
    dispatch(setSelectedGiftConfig(index));
    goGiftConfig(navigate);
  };

const goGiftConfig = (navigate: NavigateFunction): void => {
  const { parentStep, childStep } = getActiveStep();
  goNextStep(navigate, parentStep, childStep);
};

const handleStepNextFromParent =
  (navigate: NavigateFunction, location: Location): AppThunkAction<void> =>
  async dispatch =>
    dispatch(validateGiftConfigsThunk())
      .then(() => {
        navigate(
          `${location.pathname}?parentStep=${ParentSteps.CONFIRMATION}&childStep=${ConfirmationChildSteps.CONFIRMATION}`
        );
      })
      .catch(() => {
        dispatch(
          validateProjectDraftFailure(ERROR_MESSAGE_FOR_PAGE_VALIDATION)
        );
      });

const onConfirmedDeletion =
  (index: number): AppThunkAction<void> =>
  async dispatch => {
    await dispatch(initilizeSelectedGiftConfigIndex());
    await dispatch(deleteGiftConfig(index));
  };

const mapStateToProps = (state: AppState) => ({
  giftConfigsForDisplay: getGiftConfigsForDisplay(
    state.selectedProjectDraft.projectJson.giftConfigs,
    state.contents
  ),
  isBulkSolutionSystemSelected: isBulkSolutionSystemSelected(state),
  isGcpSolutionSystemSelected: isGcpSolutionSystemSelected(state),
  isApiSolutionSystemSelected: isApiSolutionSystemSelected(state),
  errors: state.errors
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
  onMount: () => dispatch(onMount()),
  createGiftConfig: (navigate: NavigateFunction) =>
    dispatch(createGiftConfig(navigate)),
  selectGiftConfig: (
    navigate: NavigateFunction,
    location: Location,
    index: number
  ) => dispatch(selectGiftConfig(navigate, location, index)),
  handleStepNextFromParent: (navigate: NavigateFunction, location: Location) =>
    dispatch(handleStepNextFromParent(navigate, location)),
  onConfirmedDeletion: (index: number) => {
    dispatch(onConfirmedDeletion(index));
  }
});

export type GiftConfigsPropsMappedFromState = ReturnType<
  typeof mapStateToProps
>;
export type GiftConfigsPropsMappedFromDispatch = ReturnType<
  typeof mapDispatchToProps
>;

export default connect(mapStateToProps, mapDispatchToProps)(GiftConfigs);
